import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { capRegex } from '@shared/constants/regex.const';
import { sedeAmministrativaErrorMessage, sedeRecapitoErrorMessage } from '../common/consts/validation-message.const';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from './helpers/dati-identificativi-form-validators.helper';

export const datiIdentificativiSediRules: FormGroupRules = {
    my_CapSedeLegale: (form: FormGroup) => {
        const capControl = getDatiIdentificativiControl(form, 'my_CapSedeLegale');
        const capValue: string = capControl?.value ?? '';
        const errors = capControl.errors ?? {};

        addRemoveError(!isNilOrEmpty(capValue) && !capRegex.test(capValue), errors, 'my_CapSedeLegale', {
            status: 'Attenzione: CAP non valido',
            message: 'CAP non valido. Deve essere composto da 5 numeri',
        });

        capControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_IndirizzoSedeAmministrativa: (form: FormGroup) => {
        specificareSedeHelper(
            form,
            'my_IndirizzoSedeAmministrativa',
            'my_CapSedeAmministrativa',
            'my_IndirizzoSedeAmministrativa',
            'my_ComuneSedeAmministrativa',
            sedeAmministrativaErrorMessage
        );
    },

    my_ComuneSedeAmministrativa: (form: FormGroup) => {
        specificareSedeHelper(
            form,
            'my_ComuneSedeAmministrativa',
            'my_CapSedeAmministrativa',
            'my_IndirizzoSedeAmministrativa',
            'my_ComuneSedeAmministrativa',
            sedeAmministrativaErrorMessage
        );
    },

    my_CapSedeAmministrativa: (form: FormGroup) => {
        specificareSedeHelper(
            form,
            'my_CapSedeAmministrativa',
            'my_CapSedeAmministrativa',
            'my_IndirizzoSedeAmministrativa',
            'my_ComuneSedeAmministrativa',
            sedeAmministrativaErrorMessage
        );
        capHelper(form, 'my_CapSedeAmministrativa', 'my_IndirizzoSedeAmministrativa', 'my_ComuneSedeAmministrativa');
    },

    my_IndirizzoSedeRecapito: (form: FormGroup) => {
        specificareSedeHelper(form, 'my_IndirizzoSedeRecapito', 'my_CapSedeRecapito', 'my_IndirizzoSedeRecapito', 'my_ComuneSedeRecapito', sedeRecapitoErrorMessage);
        const my_IndirizzoSedeRecapitoControl = getDatiIdentificativiControl(form, 'my_IndirizzoSedeRecapito');
        const my_ObbligoSede: string = getDatiIdentificativiControl(form, 'my_ObbligoRilevazioneSedeRecapito')?.value;
        const showRequiredError = isNilOrEmpty(my_IndirizzoSedeRecapitoControl.value) && my_ObbligoSede === 'S';
        const errors = my_IndirizzoSedeRecapitoControl.errors ? my_IndirizzoSedeRecapitoControl.errors : {};

        addRemoveError(showRequiredError, errors, 'my_IndirizzoSedeRecapito', {
            status: REQUIRED_STATUS,
            message: 'Occorre indicare una sede di recapito.',
        });

        my_IndirizzoSedeRecapitoControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_ComuneSedeRecapito: (form: FormGroup) => {
        specificareSedeHelper(form, 'my_ComuneSedeRecapito', 'my_CapSedeRecapito', 'my_IndirizzoSedeRecapito', 'my_ComuneSedeRecapito', sedeRecapitoErrorMessage);
    },

    my_CapSedeRecapito: (form: FormGroup) => {
        specificareSedeHelper(form, 'my_CapSedeRecapito', 'my_CapSedeRecapito', 'my_IndirizzoSedeRecapito', 'my_ComuneSedeRecapito', sedeRecapitoErrorMessage);
        capHelper(form, 'my_CapSedeRecapito', 'my_IndirizzoSedeRecapito', 'my_ComuneSedeRecapito');
        const my_CapSedeRecapitoControl = getDatiIdentificativiControl(form, 'my_CapSedeRecapito');
        const my_ObbligoRilevazioneSedeRecapito: string = getDatiIdentificativiControl(form, 'my_ObbligoRilevazioneSedeRecapito')?.value;
        const showRequiredError = isNilOrEmpty(my_CapSedeRecapitoControl?.value) && my_ObbligoRilevazioneSedeRecapito === 'S';
        const errors = my_CapSedeRecapitoControl.errors ? my_CapSedeRecapitoControl.errors : {};

        addRemoveError(showRequiredError, errors, 'my_CapSedeRecapito', {
            status: REQUIRED_STATUS,
        });

        my_CapSedeRecapitoControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const specificareSedeHelper = (form: FormGroup, targetPath: string, capPath: string, indirizzoPath: string, comunePath: string, errorMessage: string): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const targetControl = getControl(datiIdentificativiGroup, targetPath);
    const cap: string = getControl(datiIdentificativiGroup, capPath)?.value;
    const indirizzo: string = getControl(datiIdentificativiGroup, indirizzoPath)?.value;
    const comune: string = getControl(datiIdentificativiGroup, comunePath)?.value;
    const showFullAddressError = (isNilOrEmpty(cap) && !isNilOrEmpty(indirizzo)) || (isNilOrEmpty(cap) && !isNilOrEmpty(comune));
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(showFullAddressError, errors, targetPath, {
        status: 'Attenzione: specificare indirizzo, Comune e CAP',
        message: errorMessage,
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};

const capHelper = (form: FormGroup, capPath: string, indirizzoPath: string, comunePath: string): void => {
    const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
    const capControl = getControl(datiIdentificativiGroup, capPath);
    const capValue: string = capControl?.value;
    const indirizzo: string = getControl(datiIdentificativiGroup, indirizzoPath)?.value;
    const comune: string = getControl(datiIdentificativiGroup, comunePath)?.value;
    const showError = (!capRegex.test(capValue) && !isNilOrEmpty(indirizzo)) || (!capRegex.test(capValue) && !isNilOrEmpty(comune));
    const errors = capControl.errors ? capControl.errors : {};

    addRemoveError(showError, errors, capPath, {
        status: 'Attenzione: CAP non valido',
        message: 'CAP non valido. Deve essere composto da 5 numeri',
    });

    capControl.setErrors(isEqual(errors, {}) ? null : errors);
};
